import React, { useCallback, useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import DialogScreen from '../DialogScreen'
import Text from '../Text'
import FilterEmptySearchState from '../filter/FilterEmptySearchState'
import { SelectContact } from '../onboarding/atoms/SelectContact'
import Checkbox from '../formControl/Checkbox'
import Button from '../Button'
import FilterPanel from '../filter/FilterPanel'

// Utils
import Icon from '../icon/Icon'

// Utils
import { useMemberFilters } from '../../hooks/useMemberFilters'
import { useAppState } from '../../context/AppContext'

// Styles
import { shadows } from '../../styles/effects'
import { mqFrom } from '../../styles/mediaQueries'

// Types
import { ICompanyMember } from '../../common/models/Company'

export enum PeoplePickerVersion {
  GIVE = 'give-feedback',
  PLAN = 'plan-feedback',
}

interface IProps {
  title: string
  subtitle: string
  topComponents?: React.ReactNode
  bottomComponents?:
    | React.ReactNode
    | ((ctx: { isFiltering: boolean }) => React.ReactNode)
  renderBottomComponentsLast?: boolean
  hideCloseButton?: boolean
  onClickBackButton?: () => void
  closeModal: () => void
  className?: string
  peoplePickerVersion?: {
    members: ICompanyMember[]
    action: (member: ICompanyMember) => void
    version?: PeoplePickerVersion
    multipleSelect?: boolean
    selectedMemberIds?: string[]
    requestPendingIds?: string[]
    notIncludedMembers?: string[]
  }
}

const ContentModal: React.FC<IProps> = ({
  title,
  subtitle,
  topComponents,
  bottomComponents,
  hideCloseButton,
  onClickBackButton,
  closeModal,
  className,
  peoplePickerVersion,
  renderBottomComponentsLast,
}) => {
  const { t } = useTranslation()
  const { theme, company } = useAppState()
  const [height, setHeight] = useState<number>(0)

  const filters = useMemberFilters(company, {
    team: true,
    job: true,
    culturalPhase: false,
  })
  const [searchTerm, setSearchTerm] = useState<string>('')

  const visibleMembers = useMemo(() => {
    return (peoplePickerVersion?.members || [])
      .filter((employee) =>
        peoplePickerVersion?.notIncludedMembers
          ? !peoplePickerVersion.notIncludedMembers.includes(employee._id)
          : true
      )
      .filter(filters.matches)
      .filter((m) => m.name.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [peoplePickerVersion?.members, filters.matches, searchTerm])

  const isFiltering = filters.hasActiveFilters || searchTerm.length > 0

  const clearAllFilters = () => {
    filters.clearAll()
    setSearchTerm('')
  }

  const measuredRef = useCallback(
    (node) => {
      if (node !== null) {
        setHeight(node.getBoundingClientRect().height)
      }
    },
    [topComponents, peoplePickerVersion, bottomComponents, filters.chips.length]
  )

  useEffect(() => {
    if (!window) return
    window.addEventListener('resize', close)

    return () => {
      window.removeEventListener('resize', close)
    }
  }, [])

  const close = () => {
    closeModal()
  }

  const resolvedBottomComponents =
    typeof bottomComponents === 'function'
      ? bottomComponents({ isFiltering })
      : bottomComponents

  return (
    <ModalContainer
      className={className}
      noCloseButton
      onDismiss={close}
      content={{
        variant: 'purple',
        content: (
          <Modal>
            {!hideCloseButton && (
              <CloseIcon
                type="Close"
                color={theme.colors.text72}
                size={16}
                onClick={close}
              />
            )}
            <Top>
              <div ref={measuredRef}>
                <TitleButtonWrapper>
                  {onClickBackButton && (
                    <Button
                      icon={'ChevronLeft'}
                      colorVariant="secondary"
                      onClick={onClickBackButton}
                    />
                  )}

                  <div>
                    <Text textStyle="displayM">{title}</Text>
                    <Text marginTop={4} marginBottom={24}>
                      {subtitle}
                    </Text>
                  </div>
                </TitleButtonWrapper>

                {peoplePickerVersion && (
                  <FilterPanel
                    filters={filters}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    countLabel={`${visibleMembers.length} ${t(
                      'general.users.member',
                      {
                        count: visibleMembers.length,
                      }
                    ).toLowerCase()}`}
                  />
                )}

                {topComponents}
              </div>
            </Top>
            <Bottom height={height}>
              {!renderBottomComponentsLast && resolvedBottomComponents}
              {peoplePickerVersion && (
                <>
                  <Text
                    textStyle="labelSmallCaps"
                    color="text72"
                    marginBottom={12}
                    marginTop={12}
                  >
                    {isFiltering
                      ? t('general.forms.searchResults')
                      : t('useTeamFilters.labelEveryone')}
                  </Text>
                  {visibleMembers.length > 0 ? (
                    visibleMembers.map((employee) => {
                      const requestPending = peoplePickerVersion.requestPendingIds?.includes(
                        employee._id
                      )
                      const selected = peoplePickerVersion?.selectedMemberIds?.includes(
                        employee._id
                      )
                      const disabled =
                        requestPending && !peoplePickerVersion.version
                      return (
                        <SelectContact
                          key={`contact-type-${employee._id}`}
                          data-cy={`select-member-${employee._id}`}
                          contactMember={employee}
                          title={employee.name}
                          selected={selected}
                          actionLabel={
                            requestPending
                              ? peoplePickerVersion.version &&
                                peoplePickerVersion.version ===
                                  PeoplePickerVersion.GIVE
                                ? t('general.actions.continue')
                                : t('general.states.alreadyRequested')
                              : peoplePickerVersion.version ===
                                PeoplePickerVersion.GIVE
                              ? t('general.actions.startNow')
                              : t('general.actions.select')
                          }
                          disabled={disabled}
                          action={() =>
                            !disabled && peoplePickerVersion.action(employee)
                          }
                          actionElement={
                            !requestPending &&
                            peoplePickerVersion.multipleSelect && (
                              <StyledCheckbox
                                color="primary"
                                checked={selected}
                              />
                            )
                          }
                        />
                      )
                    })
                  ) : (
                    <FilterEmptySearchState
                      filters={filters}
                      searchTerm={searchTerm}
                      onClear={clearAllFilters}
                    />
                  )}
                </>
              )}
              {renderBottomComponentsLast && resolvedBottomComponents}
            </Bottom>
          </Modal>
        ),
      }}
    />
  )
}

export default ContentModal

const ModalContainer = styled(DialogScreen)`
  > div {
    overflow: hidden;
  }
`

const Modal = styled.div`
  border-radius: 12px;
  box-shadow: ${shadows.depthTwo};
  padding-top: 32px;
  height: calc(100vh - 176px);
  background-color: ${({ theme }) => theme.colors.lightest};
  overflow: hidden;
  position: relative;

  ${mqFrom.M`
    padding-top: 48px;
  `}
`

const CloseIcon = styled(Icon)`
  position: absolute;
  cursor: pointer;
  top: 24px;
  right: 24px;
  margin: 0;

  &:hover {
    svg path {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }
`

const Top = styled.div`
  padding: 0 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.textLighter};

  ${mqFrom.M`
    padding: 0 48px;
  `}
`

const Bottom = styled.div<{ height: number }>`
  background-color: ${({ theme }) => theme.colors.primaryLight};
  overflow-y: auto;
  height: calc(100vh - ${({ height }) => `(${height}px + 176px + 32px)`});
  padding: 24px;

  ${mqFrom.M`
    padding: 24px 48px;
  `}

  ${mqFrom.M`
  height: calc(100vh - ${({ height }: { height: number }) =>
    `(${height}px + 176px + 48px)`});
  `}
`

const TitleButtonWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 24px;

  button {
    height: 32px;
    width: 40px;
  }
`

const StyledCheckbox = styled(Checkbox)`
  margin-left: auto;
`
